import { useState, useEffect } from "react";
import { Zap, MapPin, Clock, ChevronDown, AlertTriangle } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const AVAILABILITY_STATES = [
  { label: "Available Now", color: "#22E584", pulse: true },
  { label: "3 Mechanics Nearby", color: "#22E584", pulse: true },
  { label: "Next: 12 min", color: "#f97316", pulse: false },
];

export default function Hero({ onBookNow, onUrgent }) {
  const [availIdx, setAvailIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [urgentMode, setUrgentMode] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    const interval = setInterval(
      () => setAvailIdx((i) => (i + 1) % AVAILABILITY_STATES.length),
      3500
    );
    return () => { clearTimeout(t); clearInterval(interval); };
  }, []);

  // Parallax background scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const bg = document.querySelector(".hero-bg-parallax");
      if (bg) {
        bg.style.transform = `translateY(${window.scrollY * 0.35}px) scale(1.08)`;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const avail = AVAILABILITY_STATES[availIdx];

  const handleUrgent = () => {
    setUrgentMode(!urgentMode);
    onUrgent && onUrgent(!urgentMode);
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Parallax Background image */}
      <img
        src="/hero-bg.webp"
        alt=""
        fetchPriority="high"
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover hero-bg-parallax"
      />
      {/* Gradient overlays */}
      <div className={`absolute inset-0 ${isDark ? "bg-gradient-to-r from-[#050507] via-[#050507]/85 to-[#050507]/40" : "bg-gradient-to-r from-[#0F172A]/90 via-[#0F172A]/75 to-[#0F172A]/30"}`} />
      <div className={`absolute inset-0 ${isDark ? "bg-gradient-to-t from-[#050507] via-transparent to-transparent" : "bg-gradient-to-t from-[#0F172A]/60 via-transparent to-transparent"}`} />

      {/* Grid pattern */}
      <div className="absolute inset-0 grid-pattern opacity-40" />

      {/* Accent orbs */}
      <div className={`absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-[120px] pointer-events-none ${isDark ? "bg-[#00F0FF]/5" : "bg-brand-blue/10"}`} />
      <div className={`absolute bottom-1/3 left-1/2 w-64 h-64 rounded-full blur-[100px] pointer-events-none ${isDark ? "bg-[#0055FF]/8" : "bg-brand-orange/8"}`} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-32 pb-24 w-full">
        <div className="max-w-2xl">
          {/* Availability badge */}
          <div
            className={`inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full glass mb-8 transition-all duration-500 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{
                backgroundColor: avail.color,
                boxShadow: `0 0 6px ${avail.color}`,
                animation: avail.pulse ? "pulse-dot 1.6s ease-in-out infinite" : "none",
              }}
            />
            <span className="text-xs font-medium text-white/80 tracking-wide">
              {avail.label}
            </span>
          </div>

          {/* Headline (Kinetic) */}
          <h1
            className={`font-display font-black text-[clamp(2.5rem,8vw,4.5rem)] tracking-tighter text-white leading-[0.95] mb-6 transition-all duration-700 delay-100 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            {["Your", "Mechanic,"].map((word, wi) => (
              <span key={wi} className="kinetic-word mr-[0.25em]">
                {word.split("").map((letter, li) => (
                  <span
                    key={li}
                    className="kinetic-letter"
                    style={{ animationDelay: `${wi * 120 + li * 40 + 200}ms` }}
                  >
                    {letter}
                  </span>
                ))}
              </span>
            ))}
            <br />
            <span
              className={`kinetic-word ${isDark ? "text-[#00F0FF]" : "text-brand-orange-light"}`}
              style={{ textShadow: isDark ? "0 0 40px rgba(0,240,255,0.5)" : "0 0 40px rgba(249,115,22,0.4)" }}
            >
              {"At Your Door.".split("").map((letter, li) => (
                <span
                  key={li}
                  className="kinetic-letter"
                  style={{ animationDelay: `${300 + li * 35}ms` }}
                >
                  {letter === " " ? "\u00A0" : letter}
                </span>
              ))}
            </span>
          </h1>

          {/* Subheadline */}
          <p
            className={`text-white/70 text-lg sm:text-xl leading-relaxed mb-10 max-w-lg transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            Certified mechanics come to you — at home, at work, anywhere in South Africa.
            Book in 60 seconds, tracked in real-time.
          </p>

          {/* CTAs (with kinetic classes) */}
          <div className={`flex flex-col sm:flex-row gap-4 mb-10 transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <button
              data-testid="hero-book-now-button"
              onClick={onBookNow}
              className={`btn-magnetic haptic flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-base transition-all ${
                isDark
                  ? "bg-[#00F0FF] text-black shadow-[0_0_32px_rgba(0,240,255,0.5)]"
                  : "bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white shadow-[0_8px_32px_rgba(249,115,22,0.45)] hover:shadow-[0_12px_40px_rgba(249,115,22,0.55)]"
              }`}
            >
              <Zap size={18} />
              Book a Mechanic Now
            </button>
            <button
              data-testid="emergency-service-button"
              onClick={handleUrgent}
              className={`btn-magnetic haptic flex items-center justify-center gap-2 px-6 py-4 rounded-xl border font-bold text-base transition-colors duration-200 ${
                urgentMode
                  ? "bg-[#FF2A5F]/20 border-[#FF2A5F] text-[#FF2A5F] shadow-[0_0_24px_rgba(255,42,95,0.4)]"
                  : "border-white/20 text-white/80 hover:border-white/40 hover:text-white backdrop-blur-sm"
              }`}
            >
              <AlertTriangle size={18} />
              {urgentMode ? "Emergency Mode ON" : "Emergency Service"}
            </button>
          </div>

          {/* Trust chips */}
          <div
            className={`flex flex-wrap gap-3 sm:gap-5 transition-all duration-700 delay-500 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            {[
              { icon: Clock, text: "~25 min ETA" },
              { icon: MapPin, text: "40 km radius" },
              { icon: Zap, text: "Qualified Technicians" },
            ].map(({ icon: Icon, text }) => (
              <div
                key={text}
                className="flex items-center gap-2 text-sm text-white/60"
              >
                <Icon size={14} className={isDark ? "text-[#00F0FF]" : "text-brand-orange-light"} />
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="text-xs text-white tracking-[0.2em] uppercase">Scroll</span>
        <ChevronDown size={16} className="text-white animate-bounce" />
      </div>
    </section>
  );
}
